import { LanguagesEnum } from '@/api/enums';
import { ReportRequest } from '@/client/reports/types/reports.type';
import { Tag } from '@/client/tags';
import { COURSE_SCORE_BY_TAG_FILTERS } from '@/common/constants';
import { LanguageContext } from '@/common/context/LanguageContext';
import { TagsSelector } from '@/components/form/selectors/TagsSelector';
import { useCourseScoreByTagReports } from '@/hooks/query/dashboard.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { CourseScoreByTagChart } from '@/ui/charts/bar-chart/CourseScoreByTagChart';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

const ChartContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  height: 500px;
  min-height: 500px;
  position: relative;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--big-padding) var(--default-padding);
  background: var(--white-main);
  margin-top: var(--default-padding);
`;

type CourseScoreByTagProps = {
  filters: ReportRequest;
};

export const CourseScoreByTag = ({ filters }: CourseScoreByTagProps) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { language } = useContext(LanguageContext);
  const storageKey = `${currentAccount?.id}-${currentUser?.id}`;

  const getStoredTags = (): Tag[] => {
    const stored = localStorage.getItem(COURSE_SCORE_BY_TAG_FILTERS);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return parsed[storageKey] || [];
  };

  const saveTagsToStorage = (tagsToSave: Tag[]) => {
    const stored = localStorage.getItem(COURSE_SCORE_BY_TAG_FILTERS);
    const existing = stored ? JSON.parse(stored) : {};

    localStorage.setItem(
      COURSE_SCORE_BY_TAG_FILTERS,
      JSON.stringify({
        ...existing,
        [storageKey]: tagsToSave,
      }),
    );
  };

  const [tags, setTags] = useState<Tag[]>(() => getStoredTags());

  const { courseScoreByTag, isLoading, refetch } = useCourseScoreByTagReports(
    currentAccount?.id as string,
    {
      ...filters,
      language: language as LanguagesEnum,
      tags: tags?.map((tag) => tag.id),
    },
  );

  useEffect(() => {
    if (tags !== undefined) {
      refetch();
    }
  }, [tags, refetch]);

  const updateTags = (newTags: Tag[]) => {
    setTags(newTags);
    saveTagsToStorage(newTags);
  };

  const handleRemoveTag = (tagId: string) => {
    const currentTags = tags || [];
    const updatedTags = currentTags.filter((t: Tag) => t.id !== tagId);
    updateTags(updatedTags);
  };

  const handleAvailableTagsLoaded = useCallback(
    (loadedTags: Tag[]) => {
      const storedTags = getStoredTags();
      if (storedTags.length === 0) {
        setTags(loadedTags);
        saveTagsToStorage(loadedTags);
      }
    },
    [storageKey],
  );

  return (
    <ChartContainer id="course-score-by-tag-chart-container">
      <TagsSelector
        selectedTags={tags || []}
        onAddTags={updateTags}
        onRemoveTag={handleRemoveTag}
        onAvailableTagsLoaded={handleAvailableTagsLoaded}
        isDashboard
      />
      <CourseScoreByTagChart
        id="course-score-by-tag-chart"
        reportData={tags.length > 0 ? courseScoreByTag : []}
        isLoading={isLoading}
        fromDate={filters.from}
        toDate={filters.to}
      />
    </ChartContainer>
  );
};
